import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Level0WRSDetails from "./Level0WRSDetails";
import Level0WRSBoundary from "./Level0WRSBoundary";
import Level0WRSAccountingPeriodVolume from "./Level0WRSAccountingPeriodVolume";
import Level0WRSDistributionLoss from "./Level0WRSDistributionLoss";
import Level0WRSTradingLinks from "./Level0WRSTradingLinks";
import Level0WRSAdministration from "./Level0WRSAdministration";
import Level0WRSLedger from "./Level0WRSLedger";
import Level0WRSWorkflowCompleted from "./Level0WRSWorkflowCompleted";
import InfoPanel from "@components/form/InfoPanel";
import WorkflowPanels from "@components/form/WorkflowPanels";
import BoundarySampleInformation from "@components/shared/BoundarySampleInformation";
import BoundarySelected from "@components/shared/BoundarySelected";
import UploadEvidencesWorkflowForm from "@components/form/UploadEvidenceWorkflowForm";
import StepButton from "@components/shared/StepButton";
import StepInfo from "@components/shared/StepInfo";
import InformationCircleIcon from "@components/icons/InformationCircleIcon";
import {
  Level0WRSProvider,
  useLevel0WRSContext,
} from "@context/Level0WRSContext";
import {
  EvidenceProvider,
  useEvidenceContext,
} from "@context/shared/EvidenceContext";
import { WaterClassProvider } from "@context/WaterClassContext";
import { toastSuccess, toastWarning } from "@utils/toast";
import { DraftNote, EMPTY_DRAFT_NOTE } from "@components/notes/NoteComposer";
import { mergeDraftNoteIntoRow } from "@components/notes/mergeDraftNoteIntoRow";

const CreateLevel0WRSWorkflow = () => {
  return (
    <EvidenceProvider>
      <Level0WRSProvider>
        <WaterClassProvider>
          <Consumer />
        </WaterClassProvider>
      </Level0WRSProvider>
    </EvidenceProvider>
  );
};

const Consumer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    currentStep,
    stepHelpers,
    workflowCompleted,
    getDetails,
    getInfoMessages,
    details,
    setLocationCoordinates,
    getAccountingPeriodAndVolume,
    getTradingLinks,
    getAdministrationInfo,
    networkErrors,
    navigateForCancel,
    handleChangeDetails,
  } = useLevel0WRSContext();
  const { getEvidencesInfo } = useEvidenceContext();
  const [draftNote, setDraftNote] = useState<DraftNote>(EMPTY_DRAFT_NOTE);
  let panelIndex = 0;

  const steps = [
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          isOnChain={workflowCompleted}
          info={<StepInfo data={getDetails().body} />}
        >
          {t("level0wrs.create.enter_details")}
        </StepButton>
      ),
      panel: <Level0WRSDetails />,
      infoPanel: (
        <InfoPanel
          actions={[
            {
              action: () => {
                toastWarning(
                  t("level0wrs.create.info_panel.step_1.action_1_toast_warning")
                );
              },
              label: t("level0wrs.create.info_panel.step_1.action_1"),
            },
          ]}
          successes={getInfoMessages("success", 0)}
          errors={getInfoMessages("error", 0)}
        >
          <div className="space-y-2">
            <p>{t("level0wrs.create.info_panel.step_1.info_intro")}</p>
            <ul className="pl-2 space-y-2">
              <li>
                <strong>{t("level0wrs.create.name")}:</strong>{" "}
                <Trans i18nKey="level0wrs.create.info_panel.step_1.name_info" />
              </li>
              <li>
                <strong>{t("level0wrs.create.identifier")}:</strong>{" "}
                {t("level0wrs.create.info_panel.step_1.identifier_info")}
              </li>
            </ul>
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          info={<BoundarySelected selected={details.geography !== ""} />}
          isOnChain={workflowCompleted}
        >
          {t("level0wrs.create.enter_boundary")}
        </StepButton>
      ),
      panel: <Level0WRSBoundary />,
      infoPanel: (
        <InfoPanel
          warnings={[t("information.location_feature_info_warning")]}
          actions={[
            {
              label: t("level0wrs.create.info_panel.step_2.action_1"),
              action: () => {
                setLocationCoordinates({ lat: "", lng: "" });
                toastSuccess(
                  t("level0wrs.create.info_panel.step_2.action_1_toast")
                );
              },
            },
          ]}
        >
          <div className="space-y-2">
            <p>{t("level0wrs.create.info_panel.step_2.info_intro")}</p>
            <BoundarySampleInformation />
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          isOnChain={workflowCompleted}
          info={<StepInfo data={getAccountingPeriodAndVolume().body} />}
        >
          {t("level0wrs.create.year_and_volume")}
        </StepButton>
      ),
      panel: <Level0WRSAccountingPeriodVolume />,
      infoPanel: (
        <InfoPanel
          successes={getInfoMessages("success", 2)}
          errors={getInfoMessages("error", 2)}
          warnings={getInfoMessages("warning", 2)}
        >
          <div className="space-y-2">
            <p>{t("level0wrs.create.info_panel.step_3.info_intro")}</p>
            <ul className="pl-2 space-y-2">
              <li>
                <Trans
                  i18nKey={"level0wrs.create.info_panel.step_3.water_year_info"}
                />
              </li>
              <li>
                <Trans
                  i18nKey={"level0wrs.create.info_panel.step_3.volume_info"}
                />
              </li>
            </ul>
            <div className="pl-2 flex gap-2">
              <InformationCircleIcon className="text-blue-600 w-5 h-5 shrink-0" />
              <div>
                {t("level0wrs.create.info_panel.step_3.info_of_zone_volume")}
                <ul className="leading-relaxed">
                  <li>
                    {t(
                      "level0wrs.create.info_panel.step_3.info_of_zone_volume_a"
                    )}
                  </li>
                  <li>
                    {t(
                      "level0wrs.create.info_panel.step_3.info_of_zone_volume_b"
                    )}
                  </li>
                  <li>
                    {t(
                      "level0wrs.create.info_panel.step_3.info_of_zone_volume_c"
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          isOnChain={workflowCompleted}
        >
          {t("level0wrs.create.distribution_loss")}
        </StepButton>
      ),
      panel: <Level0WRSDistributionLoss />,
      infoPanel: (
        <InfoPanel
          warnings={[t("information.distribution_loss_feature_info_warning")]}
        >
          <div className="space-y-2">
            <p>{t("level0wrs.create.info_panel.distribution_loss.info_1")}</p>
            <p>{t("level0wrs.create.info_panel.distribution_loss.info_2")}</p>
            <p>{t("level0wrs.create.info_panel.distribution_loss.info_3")}</p>
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          isOnChain={workflowCompleted}
          info={<StepInfo data={getTradingLinks().body} />}
        >
          {t("level0wrs.create.trading_links")}
        </StepButton>
      ),
      panel: <Level0WRSTradingLinks />,
      infoPanel: (
        <InfoPanel>
          <div className="space-y-2">
            <p>{t("level0wrs.create.info_panel.step_4.info_intro")}</p>
            <ul className="pl-2 space-y-2">
              <li>
                <strong>
                  {t("level0wrs.create.inside_zone", {
                    identifier: details.identifier,
                  })}
                  :
                </strong>{" "}
                {t("level0wrs.create.info_panel.step_4.specify_trading")}
              </li>
              <li>
                <strong>{t("level0wrs.create.between_zones")}:</strong>{" "}
                {t(
                  "level0wrs.create.info_panel.step_4.specify_inbound_outbound"
                )}
              </li>
            </ul>
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          isOnChain={workflowCompleted}
          info={<StepInfo data={getAdministrationInfo().body} />}
        >
          {t("level0wrs.create.administration")}
        </StepButton>
      ),
      panel: <Level0WRSAdministration />,
      infoPanel: (
        <InfoPanel>
          <div className="space-y-2">
            <p>{t("level0wrs.create.info_panel.step_5.info_intro")}</p>
            <div className="flex gap-4">
              <span>
                <InformationCircleIcon className="text-yellow-600 w-5 h-5"></InformationCircleIcon>
              </span>
              {t("level0wrs.create.info_panel.step_5.info_example")}
            </div>
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          isOnChain={workflowCompleted}
          info={<StepInfo data={mergeDraftNoteIntoRow(getEvidencesInfo(), draftNote, t).body} />}
        >
          {t("common.enter_note_and_evidence")}
        </StepButton>
      ),
      panel: (
        <UploadEvidencesWorkflowForm
          referenceTable="level0_wrs"
          referenceTableId={details.id}
          onNext={(activatedEvidenceIds) => {
            handleChangeDetails(
              "activatedEvidenceIds",
              activatedEvidenceIds.map((v: any) => v)
            );
            stepHelpers.goToNextStep();
          }}
          onPrevious={stepHelpers.goToPrevStep}
          onCancel={() => navigateForCancel()}
          activatedEvidences={details.activatedEvidenceIds}
          customDropboxTitle={t("level0wrs.create.step_6.title") as string}
          newUploadDescription={
            t("level0wrs.create.step_6.description", {
              level0wrsName: details.name,
            }) as string
          }
          noteValue={draftNote}
          onNoteChange={setDraftNote}
        />
      ),
      infoPanel: (
        <InfoPanel>
          <div className="space-y-4">
            <p>{t("level0wrs.create.step_6.info_intro")}</p>
            <p>{t("level0wrs.create.step_6.info_conclusion")}</p>
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={panelIndex++}
          currentStep={currentStep}
          isOnChain={workflowCompleted}
          forceSuccess={workflowCompleted}
        >
          {t("level0wrs.create.ledger")}
        </StepButton>
      ),
      panel: workflowCompleted ? (
        <Level0WRSWorkflowCompleted />
      ) : (
        <Level0WRSLedger draftNote={draftNote} />
      ),
      infoPanel: workflowCompleted ? (
        <InfoPanel
          successes={[
            t("level0wrs.create.info_panel.workflow_completed.success", {
              name: details.name,
            }),
          ]}
          actions={[
            {
              label: t(
                "level0wrs.create.info_panel.workflow_completed.action_1"
              ),
              action: () => {
                window.location.reload();
              },
            },
            {
              label: (
                <Trans
                  i18nKey="water_class.add_level0wrs_water_class"
                  values={{
                    level0wrsIdentifier: details?.identifier,
                  }}
                />
              ),
              action: () => {
                navigate(
                  `/polestar/level1wrs/${details.level1WRS?.id}/water_classes/create?level0ResourceId=${details?.id}`
                );
              },
            },
          ]}
        >
          <p className="inline">
            {t("level0wrs.create.info_panel.workflow_completed.info_intro")}
          </p>{" "}
        </InfoPanel>
      ) : (
        <InfoPanel
          successes={[t("level0wrs.create.info_panel.step_6.default_success")]}
          errors={networkErrors}
        >
          <div className="space-y-2">
            <p>{t("level0wrs.create.info_panel.step_6.info_intro")}</p>
            <ul className="pl-2 space-y-2">
              <li>
                <strong>{t("level0wrs.create.details")}:</strong>{" "}
                {t("level0wrs.create.info_panel.step_6.details_info")}
              </li>
              <li>
                <strong>{t("level0wrs.create.boundary")}:</strong>{" "}
                {t("level0wrs.create.info_panel.step_6.boundary_info")}
              </li>
              <li>
                <strong>{t("level0wrs.create.year_and_volume")}:</strong>{" "}
                {t("level0wrs.create.info_panel.step_6.year_volume_info")}
              </li>
              <li>
                <strong>{t("level0wrs.create.trading_links")}:</strong>{" "}
                {t("level0wrs.create.info_panel.step_6.trading_links_info")}
              </li>
              <li>
                <strong>{t("level0wrs.create.administration")}:</strong>{" "}
                {t("level0wrs.create.info_panel.step_6.administration_info")}
              </li>
              <li>
                <Trans i18nKey="information.edit_info_evidence" />
              </li>
            </ul>
            <p>{t("level1wrs.create.info_panel.step_3.info_conc")}</p>
          </div>
        </InfoPanel>
      ),
    },
  ];

  return <WorkflowPanels selectedStep={currentStep} steps={steps} />;
};

export default CreateLevel0WRSWorkflow;
